import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Package, Navigation, CreditCard, AlertTriangle, TrendingUp, Eye } from 'lucide-react';
import { BACKEND_URL } from '@/config';
import { euros } from '@/utils/walletTotals';
import { StatusPill } from '@/components/design';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentMissions, setRecentMissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/stats`),
      axios.get(`${API}/admin/missions?limit=5`)
    ]).then(([s, m]) => {
      setStats(s.data);
      setRecentMissions(m.data.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const kpis = [
    { label: 'Utilisateurs', value: stats?.users?.total || 0, sub: `+${stats?.users?.today || 0} aujourd'hui`, icon: <Users size={20} />, cls: 'kpi-navy', action: () => navigate('/admin/users') },
    { label: 'GMV Total', value: euros(stats?.finance?.gmv_total), sub: 'Encaissé, en euros', icon: <CreditCard size={20} />, cls: 'kpi-teal', action: () => navigate('/admin/transactions') },
    // Commission des transporteurs + frais Colis2Ship des expéditeurs (C8), en euros
    { label: 'Revenus de la plateforme', value: euros((stats?.finance?.revenue_total ?? 0) + (stats?.finance?.sender_fee_total ?? 0)), sub: 'Commission + frais, avant frais de Stripe', icon: <TrendingUp size={20} />, cls: 'kpi-gold', action: () => navigate('/admin/transactions') },
    { label: 'Missions actives', value: stats?.missions?.active || 0, sub: `${stats?.missions?.total || 0} totales`, icon: <Navigation size={20} />, cls: 'kpi-navy', action: () => navigate('/admin/moderation') },
    { label: 'Litiges ouverts', value: stats?.missions?.disputed || 0, sub: 'Action requise', icon: <AlertTriangle size={20} />, cls: 'kpi-red', action: () => navigate('/admin/disputes') },
    { label: 'Taux de conversion', value: `${stats?.conversion_rate || 0}%`, sub: 'Missions complétées', icon: <TrendingUp size={20} />, cls: 'kpi-teal', action: null },
    { label: 'Colis actifs', value: stats?.shipments?.active || 0, sub: 'Annonces publiées', icon: <Package size={20} />, cls: 'kpi-navy', action: () => navigate('/admin/moderation') },
    { label: 'Trajets actifs', value: stats?.trips?.active || 0, sub: 'Trajets disponibles', icon: <Navigation size={20} />, cls: 'kpi-gold', action: null },
  ];

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: 'var(--f-display)', fontSize: 22, fontWeight: 800, marginBottom: 4 }}>
          Tableau de bord Administrateur
        </div>
        <div style={{ fontSize: 14, color: 'var(--ink2)' }}>Vue d'ensemble de la plateforme Colis2Ship</div>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="skeleton" style={{ height: 120 }} />)}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
          {kpis.map(k => (
            <div key={k.label} className={`kpi-card ${k.cls}`} style={{ cursor: k.action ? 'pointer' : 'default' }} onClick={k.action || undefined} data-testid={`admin-kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="kpi-icon">{k.icon}</div>
              <div className="kpi-label">{k.label}</div>
              <div className="kpi-value">{k.value}</div>
              <div className="kpi-sub">{k.sub}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="section-title" style={{ margin: 0 }}>Missions récentes</div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/moderation')}>Voir tout</button>
        </div>
        {recentMissions.length === 0 ? (
          <div style={{ color: 'var(--ink3)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Aucune mission</div>
        ) : (
          <table className="c2s-table">
            <thead>
              <tr>
                <th>ID</th><th>Trajet</th><th>Statut</th><th>Montant</th><th>Date</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentMissions.map(m => (
                <tr key={m.id}>
                  <td style={{ fontSize: 12, fontFamily: 'monospace' }}>#{m.id.slice(-8)}</td>
                  <td style={{ fontSize: 13 }}>{m.shipment?.departure_city || '-'} → {m.shipment?.arrival_city || '-'}</td>
                  <td><StatusPill domain="mission" code={m.status} /></td>
                  <td style={{ fontWeight: 700 }}>{m.contracted_price?.toFixed(2)}€</td>
                  <td style={{ fontSize: 12, color: 'var(--ink3)' }}>{new Date(m.created_at).toLocaleDateString('fr-FR')}</td>
                  <td><button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => navigate(`/app/missions/${m.id}`)}><Eye size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
